import { useCallback, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";

import type { FiCatalogResponse } from "@mapmycustomers/shared/types/entity/fieldInsight";

import { getOrganizationId } from "@app/store/iam";

import { fetchMentionCharts, openChartMention } from "../store/actions";
import {
  getChartMentionNames,
  getMentionChartsOrganizationId,
  isMentionChartsLoading,
} from "../store/selectors";

import type { ChartMentions } from "./ChartMentionsContext";

/**
 * Builds the `ChartMentionsContext` value for a surface outside the Dashboard,
 * where `@chart:<id>` markers would otherwise show as not available: it
 * fetches the chart names once per org and routes a pill click through the
 * Dashboard's own saga.
 *
 * `enabled` should say whether the content on screen actually carries a chart
 * marker, so surfaces that never show one cost nothing.
 */
const useResolvedChartMentions = (enabled: boolean): ChartMentions => {
  const dispatch = useDispatch();
  const names = useSelector(getChartMentionNames);
  const requestedOrganizationId = useSelector(getMentionChartsOrganizationId);
  const chartsLoading = useSelector(isMentionChartsLoading);
  const organizationId = useSelector(getOrganizationId);

  useEffect(() => {
    if (enabled && organizationId && requestedOrganizationId !== organizationId) {
      dispatch(fetchMentionCharts.request({ organizationId }));
    }
  }, [dispatch, enabled, organizationId, requestedOrganizationId]);

  const handleOpen = useCallback(
    (chartId: FiCatalogResponse["id"]) => dispatch(openChartMention({ chartId })),
    [dispatch]
  );

  // Also loading on the render before the effect above has requested the names.
  const loading = chartsLoading || (enabled && requestedOrganizationId !== organizationId);

  return useMemo<ChartMentions>(
    () => ({ loading, names, onOpen: handleOpen }),
    [handleOpen, loading, names]
  );
};

export default useResolvedChartMentions;
